/**
 * API Client for Trailhead Backend
 */
import axios, { AxiosInstance, AxiosError } from 'axios';

export interface Phase {
  number: number;
  name: string;
  slug: string;
  description: string;
  requires_client_input: boolean;
  revision_budget: number;
  depends_on: number[];
  status: 'completed' | 'pending_review' | 'available' | 'locked';
  agents: string[];
  deliverables?: string[];
  estimated_minutes?: number;
}

export interface Client {
  id: string;
  business_name: string;
  business_type: string;
  email: string;
  current_phase: number;
  completed_phases: number[];
  status: string;
  location: string;
  country: string;
  city?: string;
  state_province?: string;
  postal_code?: string;
  evergreen_connected: boolean;
  is_admin?: boolean;
  is_impersonating?: boolean;
}

export interface AdminClient {
  id: string;
  business_name: string;
  business_type: string;
  email: string;
  contact_name: string;
  city: string;
  state_province: string;
  postal_code?: string;
  country: string;
  current_phase: number;
  completed_phases: number[];
  status: string;
  created_at: string;
  last_login: string | null;
  unread_messages: number;
  help_requested: boolean;
  help_requested_at: string | null;
  help_request_note: string | null;
}

export interface CoachMessage {
  id: string;
  sender_type: 'client' | 'coach';
  message: string;
  page_url?: string;
  phase_slug?: string;
  phase_number?: number;
  is_read: boolean;
  created_at: string;
}

export interface Conversation {
  client_id: string;
  business_name: string;
  email: string;
  current_phase: number;
  status: string;
  unread_count: number;
  total_messages: number;
  last_message: {
    sender_type: string;
    message: string;
    created_at: string;
    page_url?: string;
    phase_slug?: string;
  } | null;
}

export interface ContentItem {
  id: string;
  phase_number: number;
  content_type: string;
  content_key: string;
  title: string;
  content: string;
  content_html?: string;
  metadata?: Record<string, any>;
  status: string;
  compliance_status: string;
  compliance_issues?: string[];
  revision_count: number;
  revision_notes?: string;
}

export interface AdminUserData {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'coach' | 'viewer';
  is_active: boolean;
  last_login: string | null;
  login_count: number;
  invited_at: string | null;
  created_at: string;
  invited_by_name?: string;
}

export interface CalendarWeek {
  week_number: number;
  start_date: string;
  end_date: string;
  month: string;
  season: 'winter' | 'spring' | 'summer' | 'fall';
  blog: {
    title: string;
    slug: string;
    type: string;
    target_keyword: string;
    reason: string;
  };
  themes: {
    seasonal: string | null;
    holidays: string | null;
  };
}

export interface BlogStageInfo {
  stage: 'not_started' | 'calendar' | 'content_ready' | 'content' | 'completed';
  posts_generated?: number;
}

class API {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests — prefer admin_token for AdminUser sessions
    this.client.interceptors.request.use((config) => {
      const adminToken = localStorage.getItem('admin_token');
      const authToken = localStorage.getItem('auth_token');
      const token = adminToken || authToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle 401 errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================================
  // AUTHENTICATION
  // ============================================================

  async requestMagicLink(email: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/auth/magic-link', { email });
    return response.data;
  }

  async verifyMagicLink(token: string): Promise<{ token: string; client: Client }> {
    const response = await this.client.post('/auth/verify', { token });
    return response.data;
  }

  async getCurrentUser(): Promise<Client> {
    const response = await this.client.get('/auth/me');
    return response.data;
  }

  async updateProfile(data: Record<string, string>): Promise<Client> {
    const response = await this.client.put('/auth/me', data);
    return response.data.client;
  }

  // ============================================================
  // PHASES
  // ============================================================

  async getBusinessTypes(): Promise<{ categories: Record<string, { label: string; types: { key: string; display_name: string }[] }> }> {
    const response = await this.client.get('/onboarding/business-types');
    return response.data;
  }

  async listPhases(): Promise<{ phases: Phase[]; team_launch?: boolean }> {
    const response = await this.client.get('/onboarding/phases');
    return response.data;
  }

  async getPhaseDetail(slug: string): Promise<Phase & { runs: any[] }> {
    const response = await this.client.get(`/onboarding/phases/${slug}`);
    return response.data;
  }

  async startPhase(slug: string, data?: Record<string, any>): Promise<{ success: boolean; results: any }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/start`, data || {});
    return response.data;
  }

  async submitPhaseInput(slug: string, data: Record<string, any>): Promise<{ success: boolean }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/input`, data);
    return response.data;
  }

  async addSpecialties(slug: string, specialties: string[]): Promise<{ success: boolean; message: string; job_id: string }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/add-specialties`, { specialties });
    return response.data;
  }

  async getPhaseReview(slug: string): Promise<{ content_items: ContentItem[]; total_items: number; strategy_explainer?: { html: string; strategy_name: string; strategy_tagline: string } }> {
    const response = await this.client.get(`/onboarding/phases/${slug}/review`);
    return response.data;
  }

  async approvePhase(slug: string): Promise<{ success: boolean; unlocked_phases: string[] }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/approve`);
    return response.data;
  }

  async requestRevision(slug: string, feedback: string): Promise<{ success: boolean; revisions_used: number; revisions_remaining: number }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/revise`, { feedback });
    return response.data;
  }

  // ============================================================
  // CONTENT
  // ============================================================

  async getContentItem(contentId: string): Promise<ContentItem> {
    const response = await this.client.get(`/onboarding/content/${contentId}`);
    return response.data;
  }

  async updateContentItem(contentId: string, data: Record<string, any>): Promise<{ success: boolean }> {
    const response = await this.client.patch(`/onboarding/content/${contentId}`, data);
    return response.data;
  }

  // ============================================================
  // BLOG (Phase 7 two-stage workflow)
  // ============================================================

  async getBlogStage(): Promise<BlogStageInfo> {
    const response = await this.client.get('/onboarding/phases/blog/stage');
    return response.data;
  }

  async updateBlogCalendarTitles(updates: { week_number: number; title: string }[]): Promise<{ success: boolean; updated_count: number }> {
    const response = await this.client.patch('/onboarding/phases/blog/calendar/titles', { updates });
    return response.data;
  }

  async approvePhaseStage(slug: string, stage: string): Promise<{ success: boolean; stage?: string }> {
    const response = await this.client.post(`/onboarding/phases/${slug}/approve`, { stage });
    return response.data;
  }

  // ============================================================
  // EVERGREEN
  // ============================================================

  async connectEvergreen(locationId: string, apiKey?: string): Promise<{ success: boolean }> {
    const response = await this.client.post('/onboarding/evergreen/connect', {
      location_id: locationId,
      api_key: apiKey,
    });
    return response.data;
  }

  async requestHelp(note?: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/onboarding/help', { note: note || '' });
    return response.data;
  }


  // ============================================================
  // UPLOADS
  // ============================================================

  async uploadLogo(file: File): Promise<{ success: boolean; logo_url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/onboarding/upload/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async uploadImage(file: File): Promise<{ success: boolean; image_url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/onboarding/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async listUploadedImages(): Promise<{ images: { filename: string; url: string; size_kb: number; category: string }[]; count: number }> {
    const response = await this.client.get('/onboarding/uploads/images');
    return response.data;
  }

  async disconnectEvergreen(): Promise<{ success: boolean }> {
    const response = await this.client.post('/onboarding/evergreen/disconnect');
    return response.data;
  }

  // ============================================================
  // ADMIN
  // ============================================================

  async getAdminStats(): Promise<any> {
    const response = await this.client.get('/admin/stats');
    return response.data;
  }

  async getClients(): Promise<{ clients: AdminClient[] }> {
    const response = await this.client.get('/admin/clients');
    return response.data;
  }

  async getClientDetail(clientId: string): Promise<any> {
    const response = await this.client.get(`/admin/clients/${clientId}`);
    return response.data;
  }

  async updateClient(clientId: string, data: Record<string, any>): Promise<any> {
    const response = await this.client.put(`/admin/clients/${clientId}`, data);
    return response.data;
  }

  async createClient(data: Record<string, string>): Promise<any> {
    const response = await this.client.post('/admin/clients', data);
    return response.data;
  }

  async deleteClient(clientId: string, action: 'suspend' | 'archive' | 'delete' = 'suspend'): Promise<any> {
    const response = await this.client.delete(`/admin/clients/${clientId}`, { data: { action } });
    return response.data;
  }

  async retryPhaseRun(clientId: string, phaseRunId: string): Promise<{ success: boolean; job_id: string; agent_name: string; message: string }> {
    const response = await this.client.post(`/admin/clients/${clientId}/phase-runs/${phaseRunId}/retry`);
    return response.data;
  }

  async impersonateClient(clientId: string): Promise<{ token: string; client: Client }> {
    const response = await this.client.post(`/admin/impersonate/${clientId}`);
    return response.data;
  }

  // ============================================================
  // COACH CHAT (Client)
  // ============================================================

  async getCoachMessages(): Promise<{ messages: CoachMessage[] }> {
    const response = await this.client.get('/coach/messages');
    return response.data;
  }

  async sendCoachMessage(message: string, context: { page_url?: string; phase_slug?: string }): Promise<{ message: CoachMessage }> {
    const response = await this.client.post('/coach/messages', { message, ...context });
    return response.data;
  }

  async getUnreadCoachCount(): Promise<{ unread_count: number }> {
    const response = await this.client.get('/coach/unread-count');
    return response.data;
  }

  async markCoachMessagesRead(): Promise<void> {
    await this.client.post('/coach/mark-read');
  }

  // ============================================================
  // COACH CHAT (Admin)
  // ============================================================

  async getConversations(): Promise<{ conversations: Conversation[]; total_unread: number }> {
    const response = await this.client.get('/coach/admin/conversations');
    return response.data;
  }

  async getConversation(clientId: string): Promise<{ client: any; messages: CoachMessage[] }> {
    const response = await this.client.get(`/coach/admin/conversations/${clientId}`);
    return response.data;
  }

  async replyToConversation(clientId: string, message: string): Promise<{ message: CoachMessage }> {
    const response = await this.client.post(`/coach/admin/conversations/${clientId}/reply`, { message });
    return response.data;
  }

  async markConversationRead(clientId: string): Promise<void> {
    await this.client.post(`/coach/admin/conversations/${clientId}/read`);
  }

  async getArchivedConversations(): Promise<{ conversations: Conversation[]; total_unread: number }> {
    const response = await this.client.get('/coach/admin/conversations?archived=true');
    return response.data;
  }

  async archiveConversation(clientId: string): Promise<void> {
    await this.client.post(`/coach/admin/conversations/${clientId}/archive`);
  }

  async unarchiveConversation(clientId: string): Promise<void> {
    await this.client.post(`/coach/admin/conversations/${clientId}/unarchive`);
  }

  // TEAM MANAGEMENT (Admin) — legacy stubs kept for compatibility
  async getTeamMembers(): Promise<{ members: any[] }> {
    const response = await this.client.get('/admin/team');
    return response.data;
  }

  async addTeamMember(email: string, name?: string): Promise<any> {
    const response = await this.client.post('/admin/team', { email, name });
    return response.data;
  }

  async toggleAdmin(memberId: string, isAdmin: boolean): Promise<any> {
    const response = await this.client.put(`/admin/team/${memberId}`, { is_admin: isAdmin });
    return response.data;
  }

  async removeTeamMember(memberId: string): Promise<void> {
    await this.client.delete(`/admin/team/${memberId}`);
  }

  // ============================================================
  // ADMIN AUTH (multi-seat AdminUser accounts)
  // ============================================================

  async adminLogin(email: string, password: string): Promise<{ token: string; user: AdminUserData }> {
    const response = await this.client.post('/admin-auth/login', { email, password });
    return response.data;
  }

  async getAdminMe(): Promise<AdminUserData> {
    const response = await this.client.get('/admin-auth/me');
    return response.data;
  }

  async sendAdminInvite(email: string, name: string, role: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/admin-auth/invite', { email, name, role });
    return response.data;
  }

  async validateInviteToken(token: string): Promise<{ valid: boolean; email: string; name: string; role: string }> {
    const response = await this.client.get(`/admin-auth/invite/${token}`);
    return response.data;
  }

  async acceptInvite(token: string, password: string): Promise<{ token: string; user: AdminUserData }> {
    const response = await this.client.post('/admin-auth/accept-invite', { token, password });
    return response.data;
  }

  async changeAdminPassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    const response = await this.client.post('/admin-auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  }

  async getAdminTeam(): Promise<{ members: AdminUserData[] }> {
    const response = await this.client.get('/admin-auth/team');
    return response.data;
  }

  async updateAdminRole(memberId: string, role: string): Promise<{ success: boolean }> {
    const response = await this.client.patch(`/admin-auth/team/${memberId}`, { role });
    return response.data;
  }

  async deactivateAdminMember(memberId: string): Promise<{ success: boolean }> {
    const response = await this.client.delete(`/admin-auth/team/${memberId}`);
    return response.data;
  }

  // CONTENT EXPORT
  async exportContent(): Promise<Blob> {
    const response = await this.client.post(`/content/export`, {}, { responseType: "blob" });
    return response.data;
  }
}

export const api = new API();
